import { useMemo, useState, type FC } from "react";
import { useStaffMode } from "../../../hooks/StaffModeProvider";

export type ProfileOfficeTab = { id: string; label: string };

export function useProfileOfficeTabSelection(tabs: ProfileOfficeTab[]): {
  activeTabId: string;
  setActiveTabId: (tabId: string) => void;
} {
  const [selectedTabId, setSelectedTabId] = useState<string | null>(null);

  const activeTabId = useMemo(() => {
    if (tabs.length === 0) {
      return "";
    }
    if (selectedTabId && tabs.some((tab) => tab.id === selectedTabId)) {
      return selectedTabId;
    }
    return tabs[0]?.id ?? "";
  }, [tabs, selectedTabId]);

  return { activeTabId, setActiveTabId: setSelectedTabId };
}

export type ProfileOfficeTabsProps = {
  tabs: ProfileOfficeTab[];
  activeTabId: string;
  onSelect: (tabId: string) => void;
  className?: string;
};

const ProfileOfficeTabs: FC<ProfileOfficeTabsProps> = ({
  tabs,
  activeTabId,
  onSelect,
  className = "",
}) => {
  const { isStaffMode } = useStaffMode();

  if (tabs.length <= 1) {
    return null;
  }

  return (
    <div
      className={`flex shrink-0 overflow-x-auto border-b border-gray-200 px-4 ${className}`.trim()}
      role="tablist"
      aria-label="Office"
    >
      {tabs.map((tab) => {
        const isActive = tab.id === activeTabId;
        return (
          <button
            key={tab.id}
            type="button"
            role="tab"
            aria-selected={isActive}
            onClick={() => onSelect(tab.id)}
            className={`whitespace-nowrap border-b-2 px-4 py-2 text-sm font-medium transition-colors duration-200 focus:outline-none ${
              isActive
                ? isStaffMode
                  ? "border-secondary text-secondary"
                  : "border-primary text-primary"
                : "border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700"
            }`}
          >
            {tab.label}
          </button>
        );
      })}
    </div>
  );
};

export default ProfileOfficeTabs;
